import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MessageContext } from '../../../context/MessageContext';
import { PlanogramService } from '../../../service/PlanogramService';
import { createPlanogramCell } from '../model/PlanogramCellModel';
import { PlanogramTransformUtils } from '../utils/PlanogramTransformUtils';
import { isPlanogramComplete as isPlanogramCompleteUtil } from '../utils/PlanogramUtils';
import { TabView, TabPanel } from 'primereact/tabview';
import PlanogramVisual from './PlanogramVisual';
import PlanogramTabular from './PlanogramTabular';
import AddEditCellSettingsDialog from '../dialogs/AddEditCellSettingsDialog';
import ChangePriceDialog from '../dialogs/ChangePriceDialog';
import ChangeDataPlanogramDialog from '../dialogs/ChangeDataPlanogramDialog';
import SchemeSettingsDialog from '../dialogs/SchemeSettingsDialog';
import CopyPlanogramToDeviceDialog from '../dialogs/CopyPlanogramToDeviceDialog';
import ChangedConfigurationDialog from '../dialogs/ChangedConfigurationDialog';
import LoadingOverlay from '../../../component/loadingoverlay/LoadingOverlay';
import { CellSettingsMode } from '../enums/CellSettingsMode';
import { PlanogramVisualMode } from '../enums/PlanogramVisualMode';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as WarningTriangleIcon } from '../../../assets/images/warning-triangle-icon.svg';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import './PlanogramDetails.scss';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import './ProductIncompleteDialog.scss';
import { LayoutContext } from '../../../context/LayoutContext';
import useNavigationContext from '../../../utils/navigationcontext/NavigationContext';
import AddProductDialog from '../../products/dialogs/AddProductDialog';
import ProductSidebar from '../../products/ProductSidebar';
import { PlanogramPendingService } from '../../../service/PlanogramPendingService';
import LockBanner from '../../../component/lockbanner/LockBanner';
import ConfirmDialog from '../../../component/confirmdialog/ConfirmDialog';

import { PlanogramEditor } from './domain/PlanogramEditor';
import { PlanogramData, PlanogramCellData, ProductOption, parseCellKey } from './domain/types';
import { usePlanogramEditor } from './hooks/usePlanogramEditor';
import { usePlanogramSelection } from './hooks/usePlanogramSelection';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import { usePlanogramLock } from './hooks/usePlanogramLock';
import { useProductSearch } from './hooks/useProductSearch';
import {
    PLANOGRAM_VIEW_QUERY_PARAM,
    getPlanogramViewByTabIndex,
    getTabIndexByPlanogramView,
    withPlanogramViewQuery,
} from '../utils/planogramViewQuery';
import ConfigurationDimensionChangeDialog from '../dialogs/ConfigurationDimensionChangeDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanogramDetailsProps {
    mode: string;
}

interface ChangedConfigurationSavePayload {
    effectiveDate: Date | null;
    applyImmediately: boolean;
}

type PendingPlanogramRow = {
    id?: string | number | null;
    uuid?: string | number | null;
    externalId?: string | number | null;
    name?: string | null;
    planogram?: {
        name?: string | null;
        [key: string]: unknown;
    } | null;
    newPlanogram?: {
        name?: string | null;
        [key: string]: unknown;
    } | null;
    implementationDate?: string | null;
    status?: string | null;
    createdAt?: string | null;
    [key: string]: unknown;
};

type PlanogramPendingMatchPayload = {
    planogramName?: string | null;
    implementationDate?: string | null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const cellHasAssignedProduct = (
    cell: PlanogramCellData | null | undefined,
): boolean => {
    if (!cell || cell?.mergeData?.isMerged) {
        return true;
    }

    if (cell.type !== 'product') {
        return false;
    }

    const product =
        cell.product as Record<string, unknown> | null | undefined;

    return Boolean(
        product &&
        (
            product.id ||
            product.externalId ||
            product.productExternalId ||
            cell.productExternalId ||
            cell.productName ||
            product.name
        ),
    );
};

const isProductMapIncomplete = (
    data: PlanogramData | null | undefined,
): boolean => {
    if (!data?.cells?.length) {
        return false;
    }

    const visibleCells = data.cells.flatMap(row =>
        row.filter(cell => cell && !cell?.mergeData?.isMerged),
    );

    return (
        visibleCells.length > 0 &&
        visibleCells.some(cell => !cellHasAssignedProduct(cell))
    );
};

const PlanogramDetails: React.FC<PlanogramDetailsProps> = ({ mode }) => {
    const { t } = useTranslation();
    const { planogramId, rows, columns, name } = useParams<{
        planogramId?: string;
        rows?: string;
        columns?: string;
        name?: string;
    }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Cast contexts as any because their JS implementations are not fully typed
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext) as any;  // eslint-disable-line @typescript-eslint/no-explicit-any

    // -----------------------------------------------------------------------
    // Context menu (kept local — UI-only, no domain mutation involved)
    // -----------------------------------------------------------------------
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [contextMenuCell, setContextMenuCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);

    // -----------------------------------------------------------------------
    // Dialog visibility (kept local — pure UI state, not planogram data)
    // -----------------------------------------------------------------------
    const [isLoading, setIsLoading] = useState(true);
    // *** GH #569: the editor's active tab now initializes from the
    // planogramView URL query param instead of a hardcoded 1 (Visual).
    // That hardcoded default was half of the original bug: arriving here
    // from a Tabular preview's Edit click always landed on Visual
    // regardless, because nothing about "which preview tab" ever made it
    // into this component at all. See handleEditorTabChange below for how
    // this stays in sync as the user switches tabs while editing, and
    // getPreviewLink further down for how the CURRENT value of this state
    // is threaded back out to Save/Cancel.
    const [activeTabIndex, setActiveTabIndex] = useState(() =>
        getTabIndexByPlanogramView(searchParams.get(PLANOGRAM_VIEW_QUERY_PARAM)),
    );
    const [showCellSettingsDialog, setShowCellSettingsDialog] = useState(false);
    const [showChangePriceDialog, setShowChangePriceDialog] = useState(false);
    const [showChangeDataDialog, setShowChangeDataDialog] = useState(false);
    const [showSchemeSettingsDialog, setShowSchemeSettingsDialog] = useState(false);
    const [showCopyToDeviceDialog, setShowCopyToDeviceDialog] = useState(false);
    const [showChangedConfigurationDialog, setShowChangedConfigurationDialog] = useState(false);
    const [showAddProductDialog, setShowAddProductDialog] = useState(false);
    const [productSidebarState, setProductSidebarState] = useState<{
        visible: boolean;
        viewMode: string;
        initialEanCode: string | null;
    }>({ visible: false, viewMode: 'NEW', initialEanCode: null });
    const [selectedCellData, setSelectedCellData] = useState<{
        rowIndex?: number;
        colIndex?: number;
        cell?: Record<string, unknown>;
        selectedCells?: Set<string>;
    } | null>(null);
    const [cellSettingsMode, setCellSettingsMode] = useState<string>(CellSettingsMode.ADD);
    const [showOnlyEmptyCells, setShowOnlyEmptyCells] = useState(false);
    const [showPlanogramCells, setShowPlanogramCells] = useState(false);
    const [showClearSelectedDialog, setShowClearSelectedDialog] = useState(false);
    const [productReloadKey, setProductReloadKey] = useState(0);
    const [showSaveWithoutProductsDialog, setShowSaveWithoutProductsDialog] = useState(false);
    
    const [
        showConfigurationDimensionChangeDialog,
        setShowConfigurationDimensionChangeDialog,
    ] = useState(false);

    const [
        configurationDimensionAssignedDevicesCount,
        setConfigurationDimensionAssignedDevicesCount,
    ] = useState(0);

    const {
        setShowPageTitle,
        setShowBackButton,
        setBackButtonLabel,
        setBackButtonNavigateTo,
        setBackButtonHandleOnClick,
        setSwitchMenuHandleOnClick,
    } = useContext(LayoutContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any

    const navigationContext = useNavigationContext();
    const getBackLink = useCallback(
        () => `/planograms?${navigationContext.getContextParam()}`,
        [navigationContext],
    );

    const lastFetchedPlanogramIdRef = useRef<string | null>(null);
    const hasInitializedRef = useRef(false);

    // -----------------------------------------------------------------------
    // Domain/hooks layer — this replaces ~25 inline handlers + ~15 useState
    // calls from the original. Each hook owns exactly one cross-cutting
    // concern; this component's job is now composition + dialog wiring.
    // -----------------------------------------------------------------------

    const handlePlanogramSaveRef = useRef<(
        navigateAfter?: boolean,
        effectiveDate?: Date | null,
        applyImmediately?: boolean,
    ) => Promise<void>>(async () => undefined);

    const unsavedGuard = useUnsavedChangesGuard({
        onNavigate: target => navigate(target),
        onSaveBeforeNavigate: () => handlePlanogramSaveRef.current(false),
    });

   

    const planogramEditor = usePlanogramEditor(null, {
        onMutate: unsavedGuard.markDirty,
        onLimitError: kind => {
            if (kind === 'columnsRange') showErrorMessage(t('planogram.create.validation.columnsRange'));
            if (kind === 'rowsRange') showErrorMessage(t('planogram.create.validation.rowsRange'));
        },
        onMergeError: () => showErrorMessage(t('planogram.merge.invalidSelection')),
    });

    const selectionState = usePlanogramSelection();
    const lock = usePlanogramLock(planogramId, mode === PlanogramVisualMode.EDIT);
    const productSearch = useProductSearch();

    const planogramData = planogramEditor.data;
    const editor = planogramEditor.editor;

    // -----------------------------------------------------------------------
    // Navigation guards — thin wrappers over unsavedGuard, preserving the
    // exact same two call sites (switch-menu, back-button) the original had.
    // -----------------------------------------------------------------------

    const handleSwitchMenuOnClick = useCallback(
        (destination: string) => unsavedGuard.guardNavigation(destination),
        [unsavedGuard],
    );
        
    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    const getTitle = useCallback((): string => {
        if (rows && columns && name) {
            return `${rows}x${columns} - ${decodeURIComponent(name)}`;
        }
        if (planogramData) {
            return `${planogramData.rows}x${planogramData.columns} - ${planogramData.name ?? ''}`;
        }
        return '';
    }, [rows, columns, name, planogramData]);

    // *** GH #569: derives the preview URL for THIS planogram, carrying
    // whatever tab is currently active in the editor through to the
    // preview's matching tab. Centralized here (rather than inlining
    // withPlanogramViewQuery at each of the three call sites below — first
    // save, edit save, cancel) so all three navigations-back-to-preview
    // are guaranteed to agree on the same activeTabIndex->view mapping,
    // and so a future change to that mapping only needs to happen once.
    //
    // Accepts an explicit id override because the NEW-planogram first-save
    // case (see handlePlanogramSave below) doesn't have planogramData's
    // externalId available yet at the point it needs this link — it only
    // gets the newly-created id back from the create() call itself.
    const getCurrentPlanogramView = useCallback(
        () => getPlanogramViewByTabIndex(activeTabIndex),
        [activeTabIndex],
    );

    const getPreviewLink = useCallback(
        (idOverride?: string | number | null) => {
            const previewId = idOverride ?? planogramData?.externalId ?? planogramData?.id ?? planogramId;
            return withPlanogramViewQuery(`/planograms/view/${previewId}`, getCurrentPlanogramView());
        },
        [planogramData?.externalId, planogramData?.id, planogramId, getCurrentPlanogramView],
    );

    // -----------------------------------------------------------------------
    // Stable refs — same purpose as the original: let the mount-only layout
    // effect always call the latest version of each callback without
    // needing to re-run when planogramData changes.
    // -----------------------------------------------------------------------
    const getTitleRef = useRef(getTitle);
    const getBackLinkRef = useRef(getBackLink);
    const handleSwitchMenuOnClickRef = useRef(handleSwitchMenuOnClick);
    const guardedBackClickRef = useRef(unsavedGuard.guardedClickHandler);
    useEffect(() => { getTitleRef.current = getTitle; }, [getTitle]);
    useEffect(() => { getBackLinkRef.current = getBackLink; }, [getBackLink]);
    useEffect(() => { handleSwitchMenuOnClickRef.current = handleSwitchMenuOnClick; }, [handleSwitchMenuOnClick]);
    useEffect(() => { guardedBackClickRef.current = unsavedGuard.guardedClickHandler; }, [unsavedGuard.guardedClickHandler]);

    // -----------------------------------------------------------------------
    // Initialise planogram
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (hasInitializedRef.current) return;
        hasInitializedRef.current = true;

        if (mode === PlanogramVisualMode.NEW && rows && columns && name) {
            const newEditor = PlanogramEditor.createEmpty(
                parseInt(rows, 10),
                parseInt(columns, 10),
                decodeURIComponent(name),
                `new-${Date.now()}`,
            );
            planogramEditor.load(newEditor);
            unsavedGuard.markClean();
            setIsLoading(false);
        } else if (mode === PlanogramVisualMode.EDIT && planogramId) {
            const fetchPlanogramData = async () => {
                if (lastFetchedPlanogramIdRef.current === planogramId) return;
                lastFetchedPlanogramIdRef.current = planogramId;

                try {
                    setIsLoading(true);
                    const data = await PlanogramService.getDetails(planogramId);

                    PlanogramTransformUtils.testApiToGridTransformation(data);

                    const transformedData: PlanogramData = {
                        ...data,
                        rows: data.rows,
                        columns: data.columns,
                        cells: PlanogramTransformUtils.apiToGrid(data),
                    };

                    setShowPlanogramCells(
                        transformedData.cells?.some(row =>
                            row.some(cell => cell?.type === 'product' && cell?.product),
                        ) ?? false,
                    );

                    planogramEditor.load(PlanogramEditor.from(transformedData));
                    unsavedGuard.markClean();
                    lock.applyLockResult(data.lockResult);
                } catch (error) {
                    console.error('Error fetching planogram:', error);
                    showErrorMessage(t('planogram.error.fetch'));
                    navigate('/planograms');
                } finally {
                    setIsLoading(false);
                }
            };

            void fetchPlanogramData();
        }

        return () => {
            hasInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, planogramId, rows, columns, name]);

    // -----------------------------------------------------------------------
    // Layout context (back button / title) — identical structure/fix to the
    // original: register once on mount via stable refs, never re-run on
    // every planogramData change (that was the "Maximum update depth
    // exceeded" bug). See original PlanogramDetails.tsx for the full
    // history of this fix; behavior is unchanged here, just relocated.
    // -----------------------------------------------------------------------

    useEffect(() => {
        const backLink = getBackLinkRef.current();

        setShowBackButton(true);
        setBackButtonNavigateTo(backLink);
        setShowPageTitle(false);

        setBackButtonHandleOnClick(() => (
            event?: { preventDefault?: () => void; stopPropagation?: () => void },
        ) => guardedBackClickRef.current(getBackLinkRef.current())(event));

        setSwitchMenuHandleOnClick(() => (dest: string) =>
            handleSwitchMenuOnClickRef.current(dest),
        );

        return () => {
            setShowBackButton(false);
            setBackButtonLabel(null);
            setBackButtonNavigateTo(null);
            setBackButtonHandleOnClick(null);
            setSwitchMenuHandleOnClick(null);
            setShowPageTitle(true);
        };
        // Intentionally run once on mount only — see comment block above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Breadcrumb label — depends on getTitle(), updates once data arrives or on rename. Memo-guarded so a search keystroke (which recreates getTitle without changing its rendered text) doesn't re-trigger the setter. */
    const lastTitleRef = useRef<string | null>(null);
    useEffect(() => {
        const currentTitle = getTitle();
        if (lastTitleRef.current === currentTitle) return;
        lastTitleRef.current = currentTitle;

        setBackButtonLabel(
            <>
                <span
                    onClick={event => {
                        const canProceed = guardedBackClickRef.current(getBackLinkRef.current())(event);
                        if (!canProceed) return;
                        navigate(getBackLinkRef.current());
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    {t('menu.planograms', 'Planograms')}
                </span>

                <span> / </span>

                <span>{currentTitle}</span>
            </>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getTitle]);

    // -----------------------------------------------------------------------
    // Context menu
    // -----------------------------------------------------------------------

    const handleContextMenu = (
        event: React.MouseEvent,
        position?: { x: number; y: number },
        cellData?: { rowIndex: number; colIndex: number },
    ) => {
        event.preventDefault();

        if (
            showContextMenu &&
            contextMenuCell &&
            cellData &&
            contextMenuCell.rowIndex === cellData.rowIndex &&
            contextMenuCell.colIndex === cellData.colIndex
        ) {
            setShowContextMenu(false);
            setContextMenuCell(null);
            return;
        }

        if (position) setContextMenuPosition(position);
        if (cellData) setContextMenuCell(cellData);
        setShowContextMenu(true);
    };

    const handleContextMenuClose = () => {
        setShowContextMenu(false);
        setContextMenuCell(null);
    };

    // -----------------------------------------------------------------------
    // Cell mutations — every one of these is now a single call into
    // usePlanogramEditor, which already marks the dirty flag via onMutate.
    // -----------------------------------------------------------------------

    const handleUpdateCellField = planogramEditor.updateCellField;
    const handleDeleteCell = planogramEditor.deleteCell;
    const handleClearCell = planogramEditor.clearCell;
    const handleDuplicateCell = planogramEditor.duplicateCell;
    const handleMergeRight = planogramEditor.mergeRight;
    const handleMergeLeft = planogramEditor.mergeLeft;
    const handleUnmerge = planogramEditor.unmerge;

    // *** GH #515 fix: cancelling a mouse copy-mode session must restore
    // whatever was at each affected cell before the session started, not
    // just clear the copy-mode UI state. usePlanogramClipboard captures a
    // snapshot per cell (via handleGetCellSnapshot, wired below) the
    // moment before each onDuplicateCell call, and replays those
    // snapshots through this handler when the user cancels —
    // applySingleCellSettings already exists for exactly this "write a
    // known cell value, ignoring whatever's currently there" shape (it's
    // the same primitive AddEditCellSettingsDialog's single-cell save
    // uses), so this is a direct reuse, not a new mutation path.
    const handleRestoreCell = (rowIndex: number, colIndex: number, snapshot: unknown) => {
        planogramEditor.applySingleCellSettings(rowIndex, colIndex, () => snapshot as PlanogramCellData);
    };

    // *** Paired with handleRestoreCell above — reads the CURRENT (i.e.
    // about-to-be-overwritten) cell data so it can be captured as the
    // restore snapshot. Deep-cloned (JSON round-trip, same technique
    // PlanogramEditor.snapshot() already uses for its own baseline) so
    // later mutations to the live grid can never retroactively change a
    // snapshot that's just sitting in clipboard state waiting for a
    // possible Cancel.
    const handleGetCellSnapshot = (rowIndex: number, colIndex: number): unknown => {
        const cell = planogramData?.cells?.[rowIndex]?.[colIndex];
        if (!cell) return null;
        return JSON.parse(JSON.stringify(cell));
    };

    const handleMergeMultiple = (rowIndex: number, colIndex: number) => {
        selectionState.enterSelectionMode();
        selectionState.toggleCell(rowIndex, colIndex);
        setShowContextMenu(false);
        setContextMenuCell(null);
    };

    const handleMergeSelected = () => {
        if (selectionState.selection.size < 2) return;

        const selectedCells =
            selectionState.selection.toArray();

        const succeeded =
            planogramEditor.mergeSelected(selectedCells);

        if (succeeded) {
            showSuccessMessage(
                t('planogram.merge.success'),
            );
        }
        selectionState.clear();
    };

    const isRowFullyMerged = (rowIndex: number): boolean =>
        editor ? editor.isRowFullyMerged(rowIndex) : false;

    const isPlanogramComplete = (data: PlanogramData | null = planogramData): boolean =>
        isPlanogramCompleteUtil(data);

    // -----------------------------------------------------------------------
    // Selection mode
    // -----------------------------------------------------------------------

    const handleSelectModeToggle = selectionState.toggleSelectionMode;

    const handleCellSelection = (rowIndex: number, colIndex: number) => {
        if (!selectionState.isSelectionMode) return;
        selectionState.toggleCell(rowIndex, colIndex);
    };

    const handleBulkCellSelection = (cellKeys: string[], shouldSelect: boolean) => {
        if (!selectionState.isSelectionMode) return;
        selectionState.setMany(cellKeys, shouldSelect);
    };

    /**
     * Entry point for "drag across 2+ cells while not yet in selection
     * mode" — see GH #504. Dragging is now itself the trigger for entering
     * selection mode (in addition to the existing explicit "Select"
     * button); a plain single click still does NOT enter selection mode,
     * by design, so this is only ever called with a 2+ cell range (the
     * grid/table components only invoke it in that case).
     */
    const handleSelectionDragCommit = (cellKeys: string[]) => {
        selectionState.enterSelectionMode();
        selectionState.setMany(cellKeys, true);
    };

    const handleClearSelectedCells = () => {
        if (selectionState.selection.isEmpty) return;
        planogramEditor.clearCells(selectionState.selection.toArray());
        selectionState.clear();
    };

    /**
     * GH #555 requirement 4: Delete/Backspace pressed while MULTIPLE cells
     * are selected must confirm before clearing, exactly like the toolbar's
     * existing "Clear cells" button. useCellClipboard's keyboard handler
     * only ever calls this for the multi-cell case (isSelectionMode with 2+
     * selected keys) — by that same condition, those targets are always
     * precisely `selectionState.selection`'s current contents (it's the one
     * and only source of "selected keys" passed into useCellClipboard as
     * `selectedKeys` on both PlanogramVisual and PlanogramTabular), so this
     * can reuse the exact same dialog-open trigger the toolbar button uses
     * rather than threading the keyboard's own target list through a
     * second, parallel confirm flow.
     */
    const handleClearSelectionRequest = () => setShowClearSelectedDialog(true);

    const handleSelectAllCells = (rowIndex = -1) => {
        if (!planogramData?.cells) return;
        selectionState.selectAll(planogramData.cells, planogramData.rows, planogramData.columns, rowIndex);
    };

    const checkAllSelectedCellsHaveProducts = (): boolean =>
        planogramData?.cells ? selectionState.selection.allHaveProducts(planogramData.cells) : true;

    const handleCancelSelection = selectionState.exitSelectionMode;

    // *** GH #569 fix: Cancel must return to the SAME tab (Tabular/Visual)
    // that was active in the editor at the moment Cancel was clicked, not
    // unconditionally to whatever the preview's own default tab is. This
    // now goes through getPreviewLink (see above), which appends the
    // current activeTabIndex's matching view onto the preview URL — the
    // unsaved-changes guard's own Save/Discard dialog flow (ConfirmDialog
    // further down, and confirmSaveAndNavigate/discardAndNavigate) reuses
    // this exact same `target`, so a Cancel that goes through that dialog
    // lands on the correct tab too, not just the direct-cancel path.
    const handlePlanogramCancel = useCallback(() => {
        const target =
            mode === PlanogramVisualMode.EDIT && planogramData?.externalId
                ? getPreviewLink(planogramData.externalId)
                : getBackLink();

        const canNavigate = unsavedGuard.guardNavigation(target);

        if (canNavigate) {
            navigate(target);
        }
    }, [mode, planogramData?.externalId, getPreviewLink, getBackLink, navigate, unsavedGuard]);

    // -----------------------------------------------------------------------
    // Change price
    // -----------------------------------------------------------------------

    const handleChangePriceForSelected = () => {
        if (selectionState.selection.isEmpty) return;

        const cellsWithoutProduct: string[] = [];
        for (const key of selectionState.selection.toArray()) {
            const { rowIndex, colIndex } = parseCellKey(key);
            const cell = planogramData?.cells?.[rowIndex]?.[colIndex];
            if (!cell || cell.type !== 'product' || !cell.product || !(cell.product as Record<string, unknown>).id) {
                cellsWithoutProduct.push(`${rowIndex + 1}-${colIndex + 1}`);
            }
        }

        if (cellsWithoutProduct.length > 0) {
            showErrorMessage(
                t('planogram.changePrice.cellsWithoutProduct', {
                    cells: cellsWithoutProduct.join(', '),
                }),
            );
            return;
        }

        setShowChangePriceDialog(true);
    };

    const handleChangePriceSave = (formData: { price: unknown; effectiveDate: unknown }) => {
        if (selectionState.selection.isEmpty) return;
        planogramEditor.applyPriceChangeToSelection(selectionState.selection.toArray(), formData);
        selectionState.clear();
        showSuccessMessage(t('planogram.changePrice.success'));
    };

    const getDefaultCurrencyFromSelectedCells = (): unknown => {
        if (selectionState.selection.isEmpty || !planogramData) return null;
        const { rowIndex, colIndex } = parseCellKey(selectionState.selection.toArray()[0]);
        const cell = planogramData.cells[rowIndex][colIndex];
        return (cell?.product as Record<string, unknown> | null)?.currency ?? null;
    };

    const getDefaultPriceFromSelectedCells = (): unknown => {
        if (selectionState.selection.isEmpty || !planogramData) return null;
        const prices: unknown[] = [];
        for (const key of selectionState.selection.toArray()) {
            const { rowIndex, colIndex } = parseCellKey(key);
            const cell = planogramData?.cells?.[rowIndex]?.[colIndex];
            if (cell && cell.type === 'product' && cell.product) {
                const price = cell.price ?? (cell.product as Record<string, unknown>).price;
                if (price !== null && price !== undefined) prices.push(price);
            }
        }
        if (prices.length > 0 && prices.every(p => p === prices[0])) return prices[0];
        return null;
    };

    const getDefaultEffectiveDateFromSelectedCells = (): Date | null => {
        if (selectionState.selection.isEmpty || !planogramData) return null;
        const effectiveDates: Date[] = [];
        for (const key of selectionState.selection.toArray()) {
            const { rowIndex, colIndex } = parseCellKey(key);
            const cell = planogramData?.cells?.[rowIndex]?.[colIndex];
            if (cell && cell.type === 'product' && cell.product) {
                const d = (cell.effectiveDate ?? (cell.product as Record<string, unknown>).effectiveDate) as Date | null | undefined;
                if (d !== null && d !== undefined) effectiveDates.push(d);
            }
        }
        if (
            effectiveDates.length > 0 &&
            effectiveDates.every(
                d => d instanceof Date && effectiveDates[0] instanceof Date && d.getTime() === effectiveDates[0].getTime(),
            )
        ) {
            return effectiveDates[0];
        }
        return null;
    };

    // -----------------------------------------------------------------------
    // Grid resize
    // -----------------------------------------------------------------------

    const getAssignedDevicesCountForLayoutChange = async (): Promise<number> => {
        const planogramUuid = getPlanogramUuidForAssignmentCheck();

        if (!planogramUuid || mode !== PlanogramVisualMode.EDIT) {
            return 0;
        }

        try {
            const response = await PlanogramService.isAssigned(planogramUuid);

            const assignedDevicesCount = Number(
                response?.assignedDevicesCount ??
                response?.tiedDevicesCount ??
                response?.count ??
                planogramData?.tiedDevicesCount ??
                0,
            );

            if (assignedDevicesCount > 0) {
                return assignedDevicesCount;
            }

            if (response?.assigned === true) {
                return Number(planogramData?.tiedDevicesCount ?? 1);
            }

            return 0;
        } catch (error) {
            console.error(
                'Failed to check assigned devices before layout change',
                error,
            );

            return Number(planogramData?.tiedDevicesCount ?? 0);
        }
    };

    const guardAssignedLayoutChange = async (
        continueAction: () => void,
    ): Promise<void> => {
        const assignedDevicesCount = await getAssignedDevicesCountForLayoutChange();

        if (assignedDevicesCount > 0) {
            setConfigurationDimensionAssignedDevicesCount(assignedDevicesCount);
            setShowConfigurationDimensionChangeDialog(true);
            return;
        }

        continueAction();
    };


    const handleAddColumn = () => {
        if (!planogramData) return;

        void guardAssignedLayoutChange(() => {
            planogramEditor.addColumn(20);
        });
    };

    const handleAddRow = () => {
        if (!planogramData) return;

        void guardAssignedLayoutChange(() => {
            planogramEditor.addRow(20);
        });
    };
    // -----------------------------------------------------------------------
    // Cell settings dialog (single + bulk "Assign product")
    // -----------------------------------------------------------------------

    const handleAssignProduct = () => {
        if (selectionState.selection.isEmpty) {
            showErrorMessage(t('planogram.selection.noCellsSelected'));
            return;
        }
        setCellSettingsMode(CellSettingsMode.BULK_EDIT);
        setSelectedCellData({ selectedCells: selectionState.selection.toSet() });
        setShowCellSettingsDialog(true);
    };

    const handleOnEditProduct = (rowIndex: number, colIndex: number) => {
        const cell = planogramData?.cells?.[rowIndex]?.[colIndex] ?? null;
        setSelectedCellData({ rowIndex, colIndex, cell: cell ?? undefined });
        setCellSettingsMode(CellSettingsMode.EDIT);
        setShowCellSettingsDialog(true);
    };

    const handleEmptyProductAction = (query: string) => {
        setProductSidebarState({ visible: true, viewMode: 'NEW', initialEanCode: query || null });
    };

    // -----------------------------------------------------------------------
    // Add product — optimistic write (full cell incl. name) then a detail
    // fetch that patches only price/qty/currency. Same two-step contract as
    // the original handleAddProduct; the split itself now lives in
    // PlanogramProductCellFactory (build vs patchDetails) and PlanogramEditor
    // (addProduct vs patchProductDetails) — this handler just sequences them.
    // -----------------------------------------------------------------------

    const handleAddProduct = useCallback(
        async (
            rowIndex: number,
            colIndex: number,
            _cell: unknown,
            productOption: ProductOption,
        ) => {
            if (!productOption) return;

            const productId =
                productOption.externalId ||
                productOption.id ||
                productOption.value ||
                productOption.systemId;

            planogramEditor.addProduct(rowIndex, colIndex, productOption);
            setShowPlanogramCells(true);
            productSearch.clear();

            try {
                const details = await productSearch.getDetailsByExternalId(String(productId ?? ''));
                if (!details) return;

                const fullProduct = productSearch.mapProductToOption(details) ?? productOption;
                planogramEditor.patchProductDetails(rowIndex, colIndex, fullProduct);
            } catch (error) {
                console.error('Error fetching product details:', error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // -----------------------------------------------------------------------
    // Cell settings save (single + bulk modes), wired to
    // AddEditCellSettingsDialog's onSave.
    // -----------------------------------------------------------------------

    const handleAddProductCellSave = (
        formData: Record<string, unknown>,
        selectedCellsForBulk: Set<string> | null = null,
        settingsMode: string = cellSettingsMode,
    ) => {
        if (!planogramData) return;

        if (settingsMode === CellSettingsMode.BULK_EDIT && selectedCellsForBulk && selectedCellsForBulk.size > 0) {
            planogramEditor.applyBulkCellSettings(
                Array.from(selectedCellsForBulk),
                (rowIndex, colIndex, prevCell) =>
                    createPlanogramCell({ rowIndex, colIndex, formData, prevCell }) as unknown as typeof prevCell,
            );
            selectionState.clear();
            setShowCellSettingsDialog(false);
            showSuccessMessage(
                t('planogram.cellSettings.bulkEditSuccess', { count: selectedCellsForBulk.size }),
            );
        } else if (selectedCellData) {
            const { rowIndex, colIndex } = selectedCellData;
            if (rowIndex === undefined || colIndex === undefined) return;
            planogramEditor.applySingleCellSettings(rowIndex, colIndex, prevCell =>
                createPlanogramCell({ rowIndex, colIndex, formData, prevCell }) as unknown as typeof prevCell,
            );
            setShowCellSettingsDialog(false);
            showSuccessMessage(t('planogram.cellSettings.saveSuccess'));
        }
    };

    // -----------------------------------------------------------------------
    // Scheme settings
    // -----------------------------------------------------------------------

    const handleCheckSchemeSettingsSave = () => {
        void guardAssignedLayoutChange(() => {
            setShowSchemeSettingsDialog(true);
        });
    };

    const handleSchemeSettingsSave = (schemeData: { rows: number; columns: number }) => {
        if (!planogramData) return;
        planogramEditor.resize(schemeData.rows, schemeData.columns);
        showSuccessMessage(t('planogram.schemeSettings.saveSuccess'));
    };

    // -----------------------------------------------------------------------
    // Rename / data change — saved immediately via the API, not part of the
    // draft/save cycle, so this intentionally does not go through
    // unsavedGuard.markDirty (matches the original's behavior).
    // -----------------------------------------------------------------------

    const handleChangeDataClick = () => setShowChangeDataDialog(true);

    const handleChangeDataSave = async (newName: string, newExternalSystemId: string) => {
        try {
            await PlanogramService.updateBasicInfo(planogramData?.externalId, {
                name: newName,
                externalSystemId: newExternalSystemId,
            });
            planogramEditor.setData({ name: newName, externalSystemId: newExternalSystemId });
            showSuccessMessage(t('planogram.rename.success'));
        } catch (e) {
            console.error('Error updating planogram basic info:', e);
            showErrorMessage(t('planogram.update.error'));
        }
    };

    // -----------------------------------------------------------------------
    // Save
    // -----------------------------------------------------------------------

    const getPlanogramUuidForAssignmentCheck = (): string | number | null => {
        return planogramData?.externalId ?? planogramData?.id ?? planogramId ?? null;
    };

    const isPlanogramAssignedForSave = useCallback(
        async (): Promise<boolean> => {
            const planogramUuid =
                planogramData?.externalId ??
                planogramData?.id ??
                planogramId ??
                null;

            if (!planogramUuid || mode !== PlanogramVisualMode.EDIT) {
                return false;
            }

            try {
                const response = await PlanogramService.isAssigned(planogramUuid);

                return (
                    response?.assigned === true ||
                    Number(
                        response?.assignedDevicesCount ??
                        response?.tiedDevicesCount ??
                        response?.count ??
                        planogramData?.tiedDevicesCount ??
                        0,
                    ) > 0
                );
            } catch (error) {
                console.error(
                    'Failed to check if planogram is assigned',
                    error,
                );

                if (editor?.hasTiedDevices?.()) {
                    return true;
                }

                return Number(planogramData?.tiedDevicesCount ?? 0) > 0;
            }
        },
        [
            mode,
            planogramId,
            planogramData?.externalId,
            planogramData?.id,
            planogramData?.tiedDevicesCount,
            editor,
        ],
    );

    const applyEffectiveDateToPlanogramData = (
        data: PlanogramData,
        effectiveDate: Date | null,
    ): PlanogramData => {
        if (!effectiveDate) {
            return data;
        }

        return {
            ...data,
            cells: data.cells.map(row =>
                row.map(cell => {
                    if (
                        !cell ||
                        cell?.mergeData?.isMerged ||
                        cell.type !== 'product' ||
                        !cell.product
                    ) {
                        return cell;
                    }

                    return {
                        ...cell,
                        effectiveDate,
                        product: {
                            ...(cell.product as Record<string, unknown>),
                            effectiveDate,
                        },
                    };
                }),
            ),
        };
    };

    const getValueAsString = (value: unknown): string | null => {
        if (value === null || value === undefined) {
            return null;
        }

        const stringValue = String(value).trim();

        return stringValue || null;
    };

    const normalizeText = (value: unknown): string => {
        return String(value ?? '')
            .trim()
            .toLowerCase();
    };

    const formatLocalDateKey = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const getDateKey = (value: unknown): string | null => {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return formatLocalDateKey(value);
        }

        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return value;
            }

            const parsedDate = new Date(value);

            if (!Number.isNaN(parsedDate.getTime())) {
                return formatLocalDateKey(parsedDate);
            }
        }

        return null;
    };

    const sleep = (milliseconds: number): Promise<void> => {
        return new Promise(resolve => {
            window.setTimeout(resolve, milliseconds);
        });
    };

    const getArrayFromResponse = <T,>(response: unknown): T[] => {
        if (Array.isArray(response)) {
            return response as T[];
        }

        if (response && typeof response === 'object') {
            const objectResponse = response as Record<string, unknown>;

            if (Array.isArray(objectResponse.content)) {
                return objectResponse.content as T[];
            }

            if (Array.isArray(objectResponse.data)) {
                return objectResponse.data as T[];
            }

            if (Array.isArray(objectResponse.items)) {
                return objectResponse.items as T[];
            }

            if (Array.isArray(objectResponse.elements)) {
                return objectResponse.elements as T[];
            }

            if (
                objectResponse.data &&
                typeof objectResponse.data === 'object'
            ) {
                return getArrayFromResponse<T>(objectResponse.data);
            }
        }

        return [];
    };

    const getPendingRowId = (row: PendingPlanogramRow): string | null => {
        return (
            getValueAsString(row.id) ??
            getValueAsString(row.externalId) ??
            getValueAsString(row.uuid)
        );
    };

    const getPendingRowPlanogramName = (row: PendingPlanogramRow): string => {
        return normalizeText(
            row.name ??
            row.newPlanogram?.name ??
            row.planogram?.name ??
            '',
        );
    };

    const isPendingRowRecentlyCreated = (
        row: PendingPlanogramRow,
        requestStartedAt: Date,
    ): boolean => {
        if (!row.createdAt) {
            return true;
        }

        const createdAtTime = new Date(row.createdAt).getTime();

        if (Number.isNaN(createdAtTime)) {
            return true;
        }

        return createdAtTime >= requestStartedAt.getTime() - 2 * 60 * 1000;
    };

    const isPendingRowStatusMarkable = (row: PendingPlanogramRow): boolean => {
        const status = String(row.status ?? '').toUpperCase();

        if (!status) {
            return true;
        }

        return (
            status === 'TO_BE_DONE' ||
            status === 'PLANNED' ||
            status === 'PENDING'
        );
    };

    const isMatchingPlanogramPendingRow = (
        row: PendingPlanogramRow,
        payload: PlanogramPendingMatchPayload,
        requestStartedAt: Date,
    ): boolean => {
        const rowId = getPendingRowId(row);

        if (!rowId) {
            return false;
        }

        const expectedPlanogramName = normalizeText(payload.planogramName);
        const rowPlanogramName = getPendingRowPlanogramName(row);

        const nameMatches =
            !expectedPlanogramName ||
            rowPlanogramName === expectedPlanogramName;

        if (!nameMatches) {
            return false;
        }

        const expectedImplementationDate = getDateKey(payload.implementationDate);
        const rowImplementationDate = getDateKey(row.implementationDate);

        const implementationDateMatches =
            !expectedImplementationDate ||
            !rowImplementationDate ||
            expectedImplementationDate === rowImplementationDate;

        if (!implementationDateMatches) {
            return false;
        }

        if (!isPendingRowRecentlyCreated(row, requestStartedAt)) {
            return false;
        }

        return isPendingRowStatusMarkable(row);
    };

    const fetchCreatedPlanogramPendingIds = async (
        payload: PlanogramPendingMatchPayload,
        requestStartedAt: Date,
    ): Promise<string[]> => {
        const response = await PlanogramPendingService.getList({
            first: 0,
            rows: 25,
            max: 25,
            sortField: 'createdAt',
            sortOrder: -1,
            showOnlyActive: false,
        });

        const rows = getArrayFromResponse<PendingPlanogramRow>(response);

        const ids = rows
            .filter(row => isMatchingPlanogramPendingRow(row, payload, requestStartedAt))
            .map(getPendingRowId)
            .filter(Boolean) as string[];

        return Array.from(new Set(ids));
    };

    const fetchCreatedPlanogramPendingIdsWithRetry = async (
        payload: PlanogramPendingMatchPayload,
        requestStartedAt: Date,
    ): Promise<string[]> => {
        const retryDelays = [0, 350, 700, 1200];

        for (const delay of retryDelays) {
            if (delay > 0) {
                await sleep(delay);
            }

            const pendingIds = await fetchCreatedPlanogramPendingIds(
                payload,
                requestStartedAt,
            );

            if (pendingIds.length > 0) {
                return pendingIds;
            }
        }

        return [];
    };

    const markCreatedPlanogramPendingsAsDone = async (
        payload: PlanogramPendingMatchPayload,
        requestStartedAt: Date,
    ): Promise<void> => {
        const pendingIds = await fetchCreatedPlanogramPendingIdsWithRetry(
            payload,
            requestStartedAt,
        );

        if (pendingIds.length === 0) {
            console.warn(
                'PlanogramDetails::applyImmediately - created pending not found on pending list, skipping markAsDone.',
            );
            return;
        }

        await Promise.all(
            pendingIds.map(pendingId =>
                PlanogramPendingService.markAsDone(pendingId),
            ),
        );
    };

    const handlePlanogramSave = useCallback(async (
        navigateAfter = true,
        effectiveDate: Date | null = null,
        applyImmediately = false,
    ) => {
        if (!planogramData) return;

        const implementationDate = applyImmediately
            ? new Date()
            : effectiveDate;

        const planogramDataToSave = applyEffectiveDateToPlanogramData(
            planogramData,
            implementationDate,
        );

        try {
            setIsLoading(true);

            const isFirstSaveOfNewPlanogram =
                mode === PlanogramVisualMode.NEW &&
                String(planogramDataToSave?.id ?? '').startsWith('new-');

            if (isFirstSaveOfNewPlanogram) {
                PlanogramTransformUtils.checkMergedCellsTransformation(planogramDataToSave);

                const planogramToSave =
                    PlanogramTransformUtils.createApiPlanogramForCreation(planogramDataToSave);

                planogramToSave.name = planogramDataToSave?.name;

                const created = await PlanogramService.create(planogramToSave);

                showSuccessMessage(t('planogram.save.success'));

                if (navigateAfter && (created?.externalId || created?.id)) {
                    const newPlanogramId = created.externalId ?? created.id;
                    navigate(getPreviewLink(newPlanogramId), { replace: true });
                }
            } else if (mode === PlanogramVisualMode.EDIT) {
                PlanogramTransformUtils.checkMergedCellsTransformation(planogramDataToSave);

                const planogramToUpdate =
                    PlanogramTransformUtils.createApiPlanogram(planogramDataToSave);

                planogramToUpdate.name = planogramDataToSave?.name;

                const requestStartedAt = new Date();

                const savedPlanogramData = await PlanogramService.update(
                    planogramDataToSave.externalId,
                    planogramToUpdate,
                );

                if (applyImmediately) {
                    await markCreatedPlanogramPendingsAsDone(
                        {
                            planogramName:
                                savedPlanogramData?.name ??
                                planogramDataToSave.name,
                            implementationDate: implementationDate
                                ? implementationDate.toISOString()
                                : new Date().toISOString(),
                        },
                        requestStartedAt,
                    );
                }

                showSuccessMessage(t('planogram.update.success'));

                if (navigateAfter) {
                    navigate(getPreviewLink(planogramDataToSave.externalId));
                }
            }

            if (editor) {
                planogramEditor.load(editor);
            }

            unsavedGuard.markClean();
        } catch (error) {
            console.error('Error saving planogram:', error);
            showErrorMessage(t('planogram.save.error'));
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planogramData, mode, editor, getPreviewLink]);

    const handleChangedConfigurationSave = async (
        payload: ChangedConfigurationSavePayload,
    ) => {
        setShowChangedConfigurationDialog(false);

        await handlePlanogramSave(
            true,
            payload.effectiveDate,
            payload.applyImmediately,
        );
    };

    const handleSaveWithoutProductsConfirm = async () => {
        setShowSaveWithoutProductsDialog(false);

        const isAssigned = await isPlanogramAssignedForSave();

        if (isAssigned) {
            setShowChangedConfigurationDialog(true);
            return;
        }

        await handlePlanogramSave(true);
    };

    const handlePlanogramSaveClick = useCallback(() => {
        void (async () => {
            if (!planogramData) return;

            if (isProductMapIncomplete(planogramData)) {
                setShowSaveWithoutProductsDialog(true);
                return;
            }

            const isAssigned = await isPlanogramAssignedForSave();

            if (isAssigned) {
                setShowChangedConfigurationDialog(true);
                return;
            }

            await handlePlanogramSave(true);
        })();
    }, [
        planogramData,
        isPlanogramAssignedForSave,
        handlePlanogramSave,
    ]);

    // Stable ref so useUnsavedChangesGuard's onSaveBeforeNavigate (bound once
    // at hook-construction time, above) always calls the latest save logic.
    useEffect(() => { handlePlanogramSaveRef.current = handlePlanogramSave; }, [handlePlanogramSave]);

    const handleApplyToDevice = () => setShowCopyToDeviceDialog(true);

    // *** GH #520 fix: this single setter is the source of truth for
    // whether EITHER tab shows real planogram cells vs the "Add products"
    // empty state. It must be passed into BOTH <PlanogramTabular> and
    // <PlanogramVisual> identically (see sharedVisualProps below) — a
    // previous version of this file passed showPlanogramCells /
    // onShowPlanogramCells only into <PlanogramVisual>'s own props, which
    // meant PlanogramTabular never received onShowPlanogramCells at all
    // and silently fell back to its own private internal state instead.
    // That meant clicking "Add products" on one tab only ever initialized
    // that one tab — the other tab had no way to find out.
    const handleShowPlanogramCells = () => {
        setShowPlanogramCells(true);
        unsavedGuard.markDirty();
    };

    // *** GH #569: tab changes now also write the planogramView query
    // param, in addition to the local activeTabIndex state — this is the
    // other half of the fix (PlanogramPreviewPage.tsx's
    // handlePreviewTabChange is the first half). Keeping the query param
    // live as the user switches tabs mid-edit means getPreviewLink (above)
    // always reflects whichever tab is CURRENTLY active, not just whatever
    // tab the editor happened to open on. `replace: true` for the same
    // reason as the preview page's identical handler — switching tabs
    // while editing shouldn't spam browser history.
    const handleEditorTabChange = useCallback(
        (index: number) => {
            setActiveTabIndex(index);

            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set(
                PLANOGRAM_VIEW_QUERY_PARAM,
                getPlanogramViewByTabIndex(index),
            );
            setSearchParams(nextSearchParams, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    if (!planogramData && !isLoading) {
        return (
            <div className="planogram-details">
                <div className="error-container">
                    <p>{t('planogram.error.notFound')}</p>
                    <Button
                        label={t('common.back')}
                        onClick={() => navigate('/planograms')}
                        className="p-button-primary"
                    />
                </div>
            </div>
        );
    }

    // *** GH #520 fix: "Add products" must initialize BOTH the Tabular and
    // Visual views at once, no matter which tab the click happened on.
    //
    // showPlanogramCells/onShowPlanogramCells now live HERE, in
    // sharedVisualProps — spread identically into both <PlanogramTabular>
    // and <PlanogramVisual> below — instead of being wired only onto
    // <PlanogramVisual> as a one-off prop. Both tabs now read and write
    // through this single shared showPlanogramCells state on
    // PlanogramDetails, so either tab's "Add products" click initializes
    // both immediately.
    const sharedVisualProps = {
        planogramData,
        isSelectionMode: selectionState.isSelectionMode,
        selectedCells: selectionState.selectedCells,
        onRenameClick: handleChangeDataClick,
        onSelectModeToggle: handleSelectModeToggle,
        onPlanogramSave: handlePlanogramSaveClick,
        onClearSelectedCells: () => setShowClearSelectedDialog(true),
        onChangePriceForSelected: handleChangePriceForSelected,
        onMergeSelected: handleMergeSelected,
        onCancelSelection: handleCancelSelection,
        allSelectedCellsHaveProducts: checkAllSelectedCellsHaveProducts(),
        onSchemeSettingsClick: handleCheckSchemeSettingsSave,
        onDeleteCell: handleDeleteCell,
        onAddProduct: handleAddProduct,
        onMergeRight: handleMergeRight,
        onMergeLeft: handleMergeLeft,
        onMergeMultiple: handleMergeMultiple,
        onUnmerge: handleUnmerge,
        onClearCell: handleClearCell,
        onClearSelectionRequest: handleClearSelectionRequest,
        onCellSelection: handleCellSelection,
        onRowFullyMerged: isRowFullyMerged,
        isLoading,
        onApplyToDevice: handleApplyToDevice,
        onAssignProduct: handleAssignProduct,
        isReadOnlyMode: lock.isReadOnlyMode,
        onBulkCellSelection: handleBulkCellSelection,
        onSelectionDragCommit: handleSelectionDragCommit,
        onEnterSelectionModeWith: selectionState.enterSelectionModeWith,
        onReplaceSelection: selectionState.replace,
        onPlanogramCancel: handlePlanogramCancel,
        showPlanogramCells,
        onShowPlanogramCells: handleShowPlanogramCells,
    };

    return (
        <div className="planogram-details">
            <LockBanner visible={!!lock.lockInfo} lockedBy={lock.lockInfo?.lockedBy} />

            <TabView
                activeIndex={activeTabIndex}
                onTabChange={e => handleEditorTabChange(e.index)}
                className="w-full"
            >
                <TabPanel header={t('planogram.tabular')}>
                    {isLoading ? (
                        <LoadingOverlay loading={isLoading} minHeight={700}>{null}</LoadingOverlay>
                    ) : planogramData ? (
                        <PlanogramTabular
                            {...sharedVisualProps}
                            showContextMenu={showContextMenu}
                            contextMenuPosition={contextMenuPosition}
                            contextMenuCell={contextMenuCell}
                            onContextMenu={handleContextMenu}
                            onContextMenuClose={handleContextMenuClose}
                            onSelectAllCells={handleSelectAllCells}
                            showOnlyEmptyCells={showOnlyEmptyCells}
                            onToggleShowOnlyEmptyCells={() => setShowOnlyEmptyCells(v => !v)}
                            showMdbId={false}
                            onEditProduct={handleOnEditProduct}
                            onChangeCellMdb={() => undefined}
                            onChangeCellEva={() => undefined}
                            invalidMdbCells={[]}
                            invalidEvaCells={[]}
                            onEditCell={() => undefined}
                            onTestCell={() => undefined}
                            manualInitializationActive={false}
                            mode="add_product"
                            onAddProduct={handleAddProduct}
                            onUpdateCellField={handleUpdateCellField}
                            productSuggestions={productSearch.suggestions}
                            productsLoading={productSearch.loading}
                            onSearchProducts={productSearch.search}
                            onClearProductSearch={productSearch.clear}
                            onDuplicateCell={handleDuplicateCell}
                            onRestoreCell={handleRestoreCell}
                            getCellSnapshot={handleGetCellSnapshot}
                            onNewProduct={handleEmptyProductAction}
                        />
                    ) : (
                        <div className="error-container">
                            <p>{t('planogram.error.notFound')}</p>
                        </div>
                    )}
                </TabPanel>

                <TabPanel header={t('planogram.visual')}>
                    {isLoading ? (
                        <LoadingOverlay loading={isLoading} minHeight={700}>{null}</LoadingOverlay>
                    ) : (
                        <PlanogramVisual
                            {...sharedVisualProps}
                            onUpdateCellField={handleUpdateCellField}
                            onAddColumn={handleAddColumn}
                            onAddRow={handleAddRow}
                            onContextMenu={handleContextMenu}
                            onEditProduct={handleOnEditProduct}
                            productSuggestions={productSearch.suggestions}
                            productsLoading={productSearch.loading}
                            onSearchProducts={productSearch.search}
                            onClearProductSearch={productSearch.clear}
                            onDuplicateCell={handleDuplicateCell}
                            onRestoreCell={handleRestoreCell}
                            getCellSnapshot={handleGetCellSnapshot}
                            onEmptyProductAction={handleEmptyProductAction}
                        />
                    )}
                </TabPanel>
            </TabView>

           <AddEditCellSettingsDialog
                visible={showCellSettingsDialog}
                onHide={() => setShowCellSettingsDialog(false)}
                onSave={handleAddProductCellSave}
                cellData={selectedCellData}
                mode={cellSettingsMode as any}
                onNewProduct={() => setShowAddProductDialog(true)}
                reloadKey={productReloadKey}
                selectedCells={cellSettingsMode === CellSettingsMode.BULK_EDIT ? selectionState.selectedCells : null}
            />
            
            <AddProductDialog
                visible={showAddProductDialog}
                onHide={() => setShowAddProductDialog(false)}
                onAdd={eanCode => {
                    setShowAddProductDialog(false);
                    setProductSidebarState({ visible: true, viewMode: 'NEW', initialEanCode: eanCode });
                }}
                onSkip={() => {
                    setShowAddProductDialog(false);
                    setProductSidebarState({ visible: true, viewMode: 'NEW', initialEanCode: null });
                }}
            />

            <ProductSidebar
                visible={productSidebarState.visible}
                onHide={() => setProductSidebarState({ visible: false, viewMode: 'NEW', initialEanCode: null })}
                onSuccess={() => {
                    setProductSidebarState({ visible: false, viewMode: 'NEW', initialEanCode: null });
                    setProductReloadKey(prev => prev + 1);
                }}
                viewMode={productSidebarState.viewMode as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                externalId={null}
                initialEanCode={productSidebarState.initialEanCode}
            />

            <ChangePriceDialog
                visible={showChangePriceDialog}
                onHide={() => setShowChangePriceDialog(false)}
                onSave={handleChangePriceSave}
                selectedCellsCount={selectionState.selection.size}
                defaultCurrency={getDefaultCurrencyFromSelectedCells()}
                defaultPrice={getDefaultPriceFromSelectedCells()}
                defaultEffectiveDate={getDefaultEffectiveDateFromSelectedCells()}
                planogramName={planogramData?.name}
                planogramId={planogramData?.id ?? planogramData?.uuid ?? planogramId}
            />

            <ChangeDataPlanogramDialog
                visible={showChangeDataDialog}
                onHide={() => setShowChangeDataDialog(false)}
                onSave={handleChangeDataSave}
                currentName={planogramData?.name}
                currentSystemId={planogramData?.systemId}
                currentExternalSystemId={planogramData?.externalSystemId}
            />

            <SchemeSettingsDialog
                visible={showSchemeSettingsDialog}
                onHide={() => setShowSchemeSettingsDialog(false)}
                onSave={handleSchemeSettingsSave}
                initialRows={planogramData?.rows}
                initialColumns={planogramData?.columns}
            />

            <CopyPlanogramToDeviceDialog
                visible={showCopyToDeviceDialog}
                onHide={() => setShowCopyToDeviceDialog(false)}
                onSave={async data => {
                    try {
                        const planogramUuid =
                            planogramData?.id ??
                            planogramData?.uuid ??
                            planogramId;

                        const deviceIds = Array.from(
                            new Set(
                                ((data?.device ?? []) as Array<Record<string, unknown>>)
                                    .map(device =>
                                        device.id ??
                                        device.uuid ??
                                        device.externalId,
                                    )
                                    .filter(Boolean),
                            ),
                        );

                        // The dialog intentionally invokes onSave once per device.
                        // Guard this contract so a future refactor cannot silently
                        // turn the workflow back into one batched POST.
                        if (deviceIds.length !== 1) {
                            throw new Error(
                                `Expected exactly one device per planogram copy request, received ${deviceIds.length}.`,
                            );
                        }

                        const planogramToUpdate =
                            PlanogramTransformUtils.createApiPlanogram(planogramData);

                        await PlanogramPendingService.copyToDevicesFromParent(
                            planogramUuid,
                            planogramToUpdate,
                            deviceIds,
                            data?.implementationDate ?? null,
                            data?.applyImmediately,
                        );
                    } catch (error) {
                        showErrorMessage(t('planogram.applyToDevice.error'));
                        throw error;
                    }
                }}
                onComplete={() => {
                    showSuccessMessage(t('planogram.applyToDevice.success'));
                }}
                transformPlanogram
                planogramData={planogramData}
                planogramTitle={planogramData?.name}
                planogramRows={planogramData?.rows}
                planogramColumns={planogramData?.columns}
                isPlanogramComplete={() => isPlanogramComplete(planogramData)}
                defaultValues={null}
                deviceExternalIdToExclude={null}
                planogramExternalIdToExclude={null}
                title={t('planogram.applyToDevice.title')}
                incompleteTitle={t('planogram.applyToDevice.incompleteTitle')}
            />

            <ChangedConfigurationDialog
                visible={showChangedConfigurationDialog}
                onHide={() => setShowChangedConfigurationDialog(false)}
                onSave={handleChangedConfigurationSave}
                assignedDevicesCount={planogramData?.tiedDevicesCount}
            />

            <ConfigurationDimensionChangeDialog
                visible={showConfigurationDimensionChangeDialog}
                onHide={() => setShowConfigurationDimensionChangeDialog(false)}
                assignedDevicesCount={configurationDimensionAssignedDevicesCount}
            />

            {/* Unsaved Changes Dialog */}
            <ConfirmDialog
                visible={unsavedGuard.showDialog}
                onHide={unsavedGuard.handleDialogHide}
                onConfirm={() => unsavedGuard.confirmSaveAndNavigate(getBackLink())}
                onCancel={() => unsavedGuard.discardAndNavigate(getBackLink())}
                title={t('planogram.device.unsavedChanges.title')}
                messageHeader={null}
                message={t('planogram.device.unsavedChanges.message')}
                actionButtonLabel={t('planogram.device.unsavedChanges.saveButton')}
                cancelButtonLabel={t('planogram.device.unsavedChanges.discardButton')}
                delayedConfirm={false}
                dialogClassName="unsaved-changes-dialog"
                >
                {null}
                </ConfirmDialog>

            <ConfirmDialog
                visible={showClearSelectedDialog}
                onHide={() => setShowClearSelectedDialog(false)}
                onCancel={() => setShowClearSelectedDialog(false)}
                onConfirm={() => {
                    handleClearSelectedCells();
                    setShowClearSelectedDialog(false);
                }}
                title={t('planogram.clearSelectedDialog.title', 'Clear selected cells?')}
                message={t(
                    'planogram.clearSelectedDialog.message',
                    'You are about to remove all assigned products from the selected cells. This action cannot be undone',
                )}
                messageHeader={null}
                actionButtonLabel={t('planogram.clearSelectedDialog.confirm', 'Clear cells')}
                cancelButtonLabel={t('common.cancel', 'Cancel')}
                delayedConfirm={false}
                variant="danger"
            >
                <div className="cells-selected-row flex align-items-center justify-content-between">
                    <label>{t('planogram.table.cells', 'CELLS')}</label>

                    <div className="flex align-items-center gap-2">
                        <span className="cells-selected-label">
                            {t('planogram.changePrice.selected', 'Selected')}:
                        </span>

                        <span className="cells-selected-count">
                            {selectionState.selection.size}
                        </span>
                    </div>
                </div>
            </ConfirmDialog>

            <ConfirmDialog
                visible={showSaveWithoutProductsDialog}
                onHide={() => setShowSaveWithoutProductsDialog(false)}
                onCancel={() => setShowSaveWithoutProductsDialog(false)}
                onConfirm={handleSaveWithoutProductsConfirm}
                title={t(
                    'planogram.saveWithoutProductsDialog.title',
                    'Product map is incomplete',
                )}
                messageHeader={
                    <div className="product-map-incomplete-warning">
                        <WarningTriangleIcon className="product-map-incomplete-warning__icon" />

                        <span className="product-map-incomplete-warning__text">
                            {t(
                                'planogram.saveWithoutProductsDialog.warning'
                            )}
                        </span>
                    </div>
                }
                message={null}
                actionButtonLabel={t(
                    'planogram.saveWithoutProductsDialog.save',
                    'Save',
                )}
                cancelButtonLabel={t('common.cancel', 'Cancel')}
                delayedConfirm={false}
                dialogClassName="product-map-incomplete-dialog"
            >
                <div className="product-map-incomplete-message">
                    {t(
                        'planogram.saveWithoutProductsDialog.message',
                        'Not all slots have assigned products. You can save the current configuration and return to it later, but the device will remain in Unconfigured status until the product map is completed. Do you want to save the current configuration and continue later?',
                    )}
                </div>
            </ConfirmDialog>

            {showContextMenu && (
                <div className="context-menu-overlay" onClick={handleContextMenuClose} />
            )}
        </div>
    );
};

export default PlanogramDetails;